<template>
    <section>
        <div class="component-box">
            <div
                v-loading="m__fetching_ads_accounts"
                class="flex flex-wrap gap-2"
            >
                <label-outside-input-select
                    :label="$t('common.advertiser_account')"
                >
                    <el-select
                        :value="m__selected_ads_account"
                        value-key="advertiser_id"
                        size="small"
                        filterable
                        :placeholder="
                            $t('input.placeholder.select_ads_account')
                        "
                        class="w-[260px]"
                        @change="handleChangeAdsAccount"
                    >
                        <el-option-group
                            :label="$t('common.advertiser_account')"
                        >
                            <el-option
                                v-for="item in m__ads_accounts"
                                :key="item.advertiser_id"
                                :label="item.name"
                                :value="item"
                            >
                                <div class="relative">
                                    <div
                                        v-if="item.belong_to_atosa"
                                        class="absolute bottom-0 -right-4 bg-brand-atosa--light util-flex-center px-1 text-white h-5"
                                    >
                                        <span class="text-[0.45rem] uppercase">
                                            atosa
                                        </span>
                                    </div>
                                    <div class="flex flex-col py-1">
                                        <span
                                            class="leading-5 text-[0.8rem] font-semibold"
                                        >
                                            {{ item.name }}
                                        </span>
                                        <span class="component-text-desc">
                                            #{{ item.advertiser_id }}
                                        </span>
                                    </div>
                                </div>
                            </el-option>
                        </el-option-group>
                    </el-select>
                </label-outside-input-select>

                <label-outside-input-select :label="$t('common.balance')">
                    <el-input
                        size="small"
                        :value="`${p__format_currency(
                            current_balance
                        )} ${currency}`"
                        class="w-[140px]"
                    ></el-input>
                </label-outside-input-select>

                <div class="!ml-auto"></div>

                <filter-wrapper
                    :filtering="filtering"
                    :handle_change_filtering="handleChangeFiltering"
                ></filter-wrapper>

                <div class="relative">
                    <el-input
                        v-model="keyword"
                        class="w-[240px]"
                        size="small"
                        clearable
                        :placeholder="
                            $t('input.placeholder.search_by_name_or_id')
                        "
                        @focus="visible_search = true"
                        @blur="visible_search = false"
                    >
                        <i
                            slot="prefix"
                            class="el-input__icon el-icon-search"
                        ></i>
                    </el-input>

                    <el-popover
                        v-model="visible_search"
                        placement="bottom-start"
                        width="240"
                        trigger="manual"
                    >
                        <ul class="py-2">
                            <li
                                v-for="item in type_keywords"
                                :key="item.value"
                                class="hover:bg-bg py-2 pl-4 cursor-pointer"
                                @click="handleSelectTypeKeyword(item.value)"
                            >
                                <span class="text-[0.8rem]">{{
                                    item.label
                                }}</span>
                            </li>
                        </ul>
                    </el-popover>
                </div>

                <FilterDatePicker
                    :ranger_date.sync="ranger_date"
                    :compare_ranger_date.sync="compare_ranger_date"
                ></FilterDatePicker>
            </div>

            <div
                v-if="filtering.length || name_filtering"
                class="flex flex-wrap justify-end gap-1 mt-2"
            >
                <el-button
                    class="mr-2"
                    size="mini"
                    type="danger"
                    plain
                    @click="handleClearAllFilter()"
                    >{{ $t('button.clear_all') }}
                </el-button>
                <el-tag
                    v-if="name_filtering"
                    size="medium"
                    type="info"
                    effect="plain"
                    closable
                    @close="handleRemoveNameFiltering()"
                >
                    {{ $t('common.name') }} : {{ name_filtering }}
                </el-tag>
                <el-tag
                    v-for="(item, index) in filtering"
                    :key="index"
                    size="medium"
                    type="info"
                    effect="plain"
                    closable
                    @close="handleRemoveFilter(index)"
                >
                    {{ renderTitleFilterTag(item) }}
                </el-tag>
            </div>
        </div>

        <Chart
            class="mt-4"
            :tiktok_account_id="tiktok_account_id"
            :ranger_date="ranger_date"
            :compare_ranger_date="compare_ranger_date"
            :filtering="filtering"
            :currency="currency"
            :advertiser_id="advertiser_id"
        ></Chart>

        <el-tabs
            ref="el_tab"
            v-model="active_pane"
            type="border-card"
            class="el-tabs-primary mt-4"
        >
            <el-tab-pane name="1">
                <template slot="label">
                    <div class="flex items-center space-x-2">
                        <p>{{ $t('common.campaign') }}</p>

                        <el-tag
                            v-if="multiple_selection_campaign.length"
                            size="small"
                            closable
                            type="info"
                            @close="multiple_selection_campaign = []"
                        >
                            {{ multiple_selection_campaign.length }}
                            <span class="lowercase">
                                {{ $t('common.selected') }}
                            </span>
                        </el-tag>
                    </div>
                </template>
                <CampaignPane
                    :belong_to_atosa="belong_to_atosa"
                    :multiple_selection.sync="multiple_selection_campaign"
                    :advertiser_id="advertiser_id"
                    :current_package="current_package"
                    :currency="currency"
                    :min_budget_campaign="min_budget_campaign"
                    :compare_ranger_date="compare_ranger_date"
                    :ranger_date="ranger_date"
                    :name_filtering="name_filtering"
                    :filtering="filtering"
                    :has_auto_ads="has_auto_ads"
                    :has_manager_ads="has_manager_ads"
                    :has_report_ads="has_report_ads"
                    :type="type"
                    :handle_select_row="handleSelectRow"
                    :tiktok_account_id="tiktok_account_id"
                ></CampaignPane>
            </el-tab-pane>
            <el-tab-pane name="2">
                <template slot="label">
                    <div class="flex items-center space-x-2">
                        <p>{{ $t('common.adgroup') }}</p>

                        <el-tag
                            v-if="multiple_selection_adgroup.length"
                            size="small"
                            closable
                            type="info"
                            @close="multiple_selection_adgroup = []"
                        >
                            {{ multiple_selection_adgroup.length }}
                            <span class="lowercase">
                                {{ $t('common.selected') }}
                            </span>
                        </el-tag>
                    </div>
                </template>

                <AdGroupPane
                    :multiple_selection.sync="multiple_selection_adgroup"
                    :multiple_selection_campaign="multiple_selection_campaign"
                    :current_package="current_package"
                    :compare_ranger_date="compare_ranger_date"
                    :ranger_date="ranger_date"
                    :advertiser_id="advertiser_id"
                    :belong_to_atosa="belong_to_atosa"
                    :min_budget_adgroup="min_budget_adgroup"
                    :name_filtering="name_filtering"
                    :filtering="filtering"
                    :has_auto_ads="has_auto_ads"
                    :has_manager_ads="has_manager_ads"
                    :has_report_ads="has_report_ads"
                    :type="type"
                    :handle_select_row="handleSelectRow"
                    :tiktok_account_id="tiktok_account_id"
                    :current_balance="current_balance"
                ></AdGroupPane>
            </el-tab-pane>
            <el-tab-pane name="3">
                <template slot="label">
                    <div class="flex items-center space-x-2">
                        <p>{{ $t('common.ad') }}</p>

                        <el-tag
                            v-if="multiple_selection_ad.length"
                            size="small"
                            closable
                            type="info"
                            @close="multiple_selection_ad = []"
                        >
                            {{ multiple_selection_ad.length }}
                            <span class="lowercase">
                                {{ $t('common.selected') }}
                            </span>
                        </el-tag>
                    </div>
                </template>
                <AdPane
                    :multiple_selection.sync="multiple_selection_ad"
                    :multiple_selection_adgroup="multiple_selection_adgroup"
                    :multiple_selection_campaign="multiple_selection_campaign"
                    :current_package="current_package"
                    :bc_id="bc_id"
                    :belong_to_atosa="belong_to_atosa"
                    :compare_ranger_date="compare_ranger_date"
                    :ranger_date="ranger_date"
                    :advertiser_id="advertiser_id"
                    :name_filtering="name_filtering"
                    :filtering="filtering"
                    :has_auto_ads="has_auto_ads"
                    :has_manager_ads="has_manager_ads"
                    :has_report_ads="has_report_ads"
                    :type="type"
                    :handle_select_row="handleSelectRow"
                    :tiktok_account_id="tiktok_account_id"
                ></AdPane>
            </el-tab-pane>
        </el-tabs>
    </section>
</template>

<script>
import CampaignPane from './campaign-pane'
import AdGroupPane from './ad-group-pane'
import AdPane from './ad-pane'
import FilterWrapper from './filter'
import FilterDatePicker from './filter-date-picker'
import selectedTiktokAccountMixin from '@/plugins/mixins/selected-tiktok-accounts'
import moment from 'moment'
import Chart from './chart'

export default {
    components: {
        CampaignPane,
        AdGroupPane,
        AdPane,
        Chart,
        FilterDatePicker,
        FilterWrapper
    },

    mixins: [selectedTiktokAccountMixin],

    data() {
        return {
            name_filtering: '',
            multiple_selection_campaign: [],
            multiple_selection_adgroup: [],
            multiple_selection_ad: [],
            active_pane: '1',
            keyword: '',
            visible_search: false,
            filtering: [],
            ranger_date: this.p__getRangerDateFromThen(7),
            compare_ranger_date: null,
            type_keywords: [
                {
                    label: this.$t('common.name'),
                    value: 'name_filtering'
                },
                {
                    label: this.$t('common.xxx_id', {
                        xxx: this.$t(`common.campaign`)
                    }),
                    value: 'campaign_ids'
                },
                {
                    label: this.$t('common.xxx_id', {
                        xxx: this.$t(`common.adgroup`)
                    }),
                    value: 'adgroup_ids'
                },
                {
                    label: this.$t('common.xxx_id', {
                        xxx: this.$t(`common.ad`)
                    }),
                    value: 'ad_ids'
                }
            ]
        }
    },

    computed: {
        current_balance() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.balance
            }

            return 0
        },

        tiktok_account_id() {
            if (this.m__selected_tiktok_account) {
                return this.m__selected_tiktok_account.id
            }

            return null
        },

        currency() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.currency
            }

            return this.p__default_currency_app
        },

        advertiser_id() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.advertiser_id
            }

            return ''
        },

        belong_to_atosa() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.belong_to_atosa
            }

            return ''
        },

        min_budget_adgroup() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.min_budget_adgroup
            }

            return ''
        },

        min_budget_campaign() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.min_budget_campaign
            }

            return '300000'
        },

        bc_id() {
            if (this.m__selected_ads_account) {
                return this.m__selected_ads_account.bc_id
            }

            return ''
        },

        type() {
            if (this.active_pane === '1') {
                return 'campaign_id'
            } else if (this.active_pane === '2') {
                return 'adgroup_id'
            } else if (this.active_pane === '3') {
                return 'ad_id'
            }
            return ''
        },

        current_package() {
            return this.$store.getters.currentPackage.s__current_package
        },

        user_info() {
            return this.$store.getters.user.s__user_info
        },

        has_auto_ads() {
            if (this.user_info?.user?.is_manager) {
                return true
            }

            if (this.user_info?.advertiser_list && this.advertiser_id) {
                const temp = this.user_info?.advertiser_list.find(
                    (item) => item.advertiser_id === this.advertiser_id
                )

                if (temp) {
                    return temp.permissions.some((item) => item === 'AUTO_ADS')
                }
            }

            return false
        },

        has_manager_ads() {
            if (this.user_info?.user?.is_manager) {
                return true
            }

            if (this.user_info?.advertiser_list && this.advertiser_id) {
                const temp = this.user_info?.advertiser_list.find(
                    (item) => item.advertiser_id === this.advertiser_id
                )

                if (temp) {
                    return temp.permissions.some(
                        (item) => item === 'MANAGER_ADS'
                    )
                }
            }

            return false
        },

        has_report_ads() {
            if (this.user_info?.user?.is_manager) {
                return true
            }

            if (this.user_info?.advertiser_list && this.advertiser_id) {
                const temp = this.user_info?.advertiser_list.find(
                    (item) => item.advertiser_id === this.advertiser_id
                )

                if (temp) {
                    return temp.permissions.some(
                        (item) => item === 'REPORT_ADS'
                    )
                }
            }

            return false
        }
    },

    watch: {
        m__selected_ads_account() {
            this.active_pane = '1'
        },

        m__selected_tiktok_account() {
            this.m__fetchAdsAccountsByTiktokAccount()
        },

        multiple_selection_campaign() {
            this.multiple_selection_adgroup = []
        },

        multiple_selection_adgroup() {
            this.multiple_selection_ad = []
        }
    },

    mounted() {
        const { st, et, filtering, name_filtering } = this.$route.query
        if (st && et && moment(st).isValid() && moment(et).isValid()) {
            this.ranger_date = [st, et]
        }

        if (filtering) {
            this.filtering = JSON.parse(filtering)
        }

        if (name_filtering) {
            this.name_filtering = JSON.parse(name_filtering)
        }

        this.m__fetchAdsAccountsByTiktokAccount()
    },

    methods: {
        handleChangeAdsAccount(value) {
            this.multiple_selection_campaign = []
            this.filtering = []

            this.$router.push({
                path: this.$route.fullPath,
                query: { filtering: JSON.stringify([]) }
            })

            this.m__handleChangeAdsAccount(value)
        },

        handleSelectTypeKeyword(type) {
            if (!this.keyword) {
                return
            }

            this.handleChangeFiltering(type, this.keyword.trim())

            this.keyword = ''
        },

        handleSelectRow(id, change_filtering = false) {
            let indexPane = 2
            let name = 'adgroup_ids'
            if (this.active_pane === '1') {
                indexPane = 2
                name = 'campaign_ids'
            } else if (this.active_pane === '2') {
                indexPane = 3
                name = 'adgroup_ids'
            } else if (this.active_pane === '3') {
                indexPane = 2
                name = 'adgroup_ids'
            }

            this.handleChangeActivePane(indexPane)
            if (change_filtering) {
                this.handleChangeFiltering(name, id)
            }
        },

        handleChangeFiltering(name, value) {
            if (!value || !name) {
                return
            }

            if (name === 'name_filtering') {
                this.name_filtering = value

                this.$router.push({
                    path: this.$route.fullPath,
                    query: { name_filtering: JSON.stringify(value) }
                })

                return
            }

            let indexFilterById = this.filtering.findIndex(
                (item) => name === item.field_name
            )

            if (indexFilterById > -1) {
                this.filtering.splice(indexFilterById, 1)
            }

            indexFilterById = this.filtering.findIndex((item) =>
                ['campaign_ids', 'adgroup_ids', 'ad_ids'].some(
                    (name) => name === item.field_name
                )
            )

            if (indexFilterById > -1) {
                this.filtering.splice(indexFilterById, 1)
            }

            const temp_filter_value = Array.isArray(value) ? value : [value]

            if (temp_filter_value.length > 0) {
                const filter = {
                    filter_value: JSON.stringify(temp_filter_value),
                    field_name: name,
                    filter_type: 'IN'
                }
                this.filtering.push(filter)
            }
            this.$router.push({
                path: this.$route.fullPath,
                query: { filtering: JSON.stringify(this.filtering) }
            })
        },

        handleChangeActivePane(index) {
            this.active_pane = `${index}`
        },

        handleClearAllFilter() {
            this.keyword = ''
            this.filtering = []
            this.name_filtering = ''

            this.$router.replace({})
        },

        handleRemoveNameFiltering() {
            this.keyword = ''
            this.name_filtering = ''

            this.$router.replace({})

            this.$router.push({
                path: this.$route.fullPath,
                query: {
                    filtering: JSON.stringify(this.filtering),
                    name_filtering: this.name_filtering
                }
            })
        },

        handleRemoveFilter(index) {
            this.filtering.splice(index, 1)
            this.keyword = ''

            this.$router.replace({})

            this.$router.push({
                path: this.$route.fullPath,
                query: {
                    filtering: JSON.stringify(this.filtering),
                    name_filtering: this.name_filtering
                }
            })
        },

        renderTitleFilterTag(item) {
            if (item.field_name === 'campaign_ids') {
                return `${this.$t('common.campaign')}: ${
                    JSON.parse(item.filter_value)[0]
                }`
            }
            if (item.field_name === 'adgroup_ids') {
                return `${this.$t('common.adgroup')}: ${
                    JSON.parse(item.filter_value)[0]
                }`
            }
            if (item.field_name === 'ad_ids') {
                return `${this.$t('common.ad')}: ${
                    JSON.parse(item.filter_value)[0]
                }`
            }
            if (item.field_name === 'campaign_status') {
                const value = JSON.parse(item.filter_value).length
                return `${this.$t('common.xxx_status', {
                    xxx: this.$t('common.campaign')
                })}: ${value}`
            }
            if (item.field_name === 'adgroup_status') {
                const value = JSON.parse(item.filter_value).length
                return `${this.$t('common.xxx_status', {
                    xxx: this.$t('common.adgroup')
                })}: ${value}`
            }
            if (item.field_name === 'ad_status') {
                const value = JSON.parse(item.filter_value).length
                return `${this.$t('common.xxx_status', {
                    xxx: this.$t('common.ad')
                })}: ${value}`
            }
        }
    }
}
</script>
